/**
 * Chat source resolver.
 *
 * Facade over ChatSourceMachine. Responsibilities:
 *   - Normalise heterogeneous provider output (v1 ad-hoc identities, v2
 *     ChatMessageV2) into ResolverNativeMessageIdentity slices the machine
 *     can compare.
 *   - Hold per-session machine state in an in-memory map (no anchor disk
 *     persistence — the machine is fully recoverable from a fresh
 *     observation).
 *   - Maintain a small ring buffer of recent transitions per session so
 *     debug surfaces (web-devconsole SourceTimeline, mesh_read_debug
 *     bundles) can show *why* the source flipped, not just what it is now.
 *
 * What this module deliberately does NOT do:
 *   - Decide which messages to display. The machine decides only between
 *     `native-history` and `pty-parser`; selecting/merging the actual
 *     message arrays stays in chat-commands.ts (A2.2).
 *   - Mutate provider modules or CLI adapters. nativeHistoryAnchoredAt on
 *     ProviderCliAdapter is gone in A2.2; the resolver is the new owner.
 *   - Persist anything to disk. A daemon restart starts every session in
 *     `Booting`, which lock-promotes on the first valid native observation
 *     of the new process.
 */

import {
  INITIAL_CHAT_SOURCE_STATE,
  transitionChatSourceState,
  type ChatSourceDecision,
  type ChatSourceLockState,
  type ChatSourceObservation,
  type ChatSourceState,
  type ChatSourceTransition,
  type ResolverNativeMessageIdentity,
} from './source-machine.js';
import type { ChatContractVersion } from '../providers/transcript-v2.js';
import { CHAT_CONTRACT_VERSION_V1, CHAT_CONTRACT_VERSION_V2 } from '../providers/transcript-v2.js';

/** Maximum transitions retained per session for debug surfaces. */
export const TRANSITION_HISTORY_LIMIT = 25;

/** Composite key uniquely identifying a chat surface for state purposes.
 *  We key by (providerType, sessionId) because the same provider may run
 *  multiple sessions and each has its own native/PTY interplay. */
export type ChatSourceSessionKey = string;

export function chatSourceSessionKey(providerType: string, sessionId: string): ChatSourceSessionKey {
  return `${providerType} ${sessionId}`;
}

interface PerSessionRecord {
  state: ChatSourceState;
  lockedSince: number | undefined;
  transitions: ChatSourceTransition[];
}

/**
 * Per-session machine store. Keyed by ChatSourceSessionKey. Daemon-lifetime
 * memory; not persisted. Tests should create their own instance to keep
 * isolation; production has a module-level singleton (CHAT_SOURCE_REGISTRY)
 * exposed at the bottom of this file.
 */
export class ChatSourceRegistry {
  private readonly records = new Map<ChatSourceSessionKey, PerSessionRecord>();

  /** Snapshot of current state for diagnostics. Does not mutate. */
  getState(key: ChatSourceSessionKey): ChatSourceState {
    return this.records.get(key)?.state ?? INITIAL_CHAT_SOURCE_STATE;
  }

  /**
   * Opaque snapshot of a session's FULL record (state + lock + transitions),
   * for callers that need to speculatively `observe()` and then roll back if
   * the resulting decision is undesirable (STICKY-NATIVE hold: a trusted
   * exact-identity session must not flip to PTY on a transient native gap).
   * Returns undefined when the session has no record yet. The snapshot is a
   * shallow copy — transitions array is copied so a later append does not
   * mutate it.
   */
  snapshotRecord(key: ChatSourceSessionKey): {
    state: ChatSourceState;
    lockedSince: number | undefined;
    transitions: ChatSourceTransition[];
  } | undefined {
    const rec = this.records.get(key);
    if (!rec) return undefined;
    return { state: rec.state, lockedSince: rec.lockedSince, transitions: [...rec.transitions] };
  }

  /** Restore a previously snapshotted record, undoing a speculative observe.
   *  Passing undefined clears the key (it had no record when snapshotted). */
  restoreRecord(
    key: ChatSourceSessionKey,
    snapshot: { state: ChatSourceState; lockedSince: number | undefined; transitions: ChatSourceTransition[] } | undefined,
  ): void {
    if (!snapshot) { this.records.delete(key); return; }
    this.records.set(key, {
      state: snapshot.state,
      lockedSince: snapshot.lockedSince,
      transitions: [...snapshot.transitions],
    });
  }

  /** Recent transitions, newest last. Empty array when nothing has happened. */
  getTransitions(key: ChatSourceSessionKey): ReadonlyArray<ChatSourceTransition> {
    return this.records.get(key)?.transitions ?? [];
  }

  /** Drop a session. Caller should invoke this when the session is destroyed
   *  to avoid unbounded growth across long-lived daemons. */
  clear(key: ChatSourceSessionKey): void {
    this.records.delete(key);
  }

  /** Drop all sessions. Test helper. */
  clearAll(): void {
    this.records.clear();
  }

  /**
   * Apply one observation, returning the decision and side-effecting the
   * stored state. The returned `nextState` is the same object now stored
   * under `key`; callers may treat the decision as authoritative without
   * re-reading.
   */
  observe(
    key: ChatSourceSessionKey,
    observation: ChatSourceObservation,
    at: number = Date.now(),
  ): ChatSourceDecision {
    const prev = this.records.get(key);
    const prevState = prev?.state ?? INITIAL_CHAT_SOURCE_STATE;
    const prevLockedSince = prev?.lockedSince;

    const result = transitionChatSourceState(prevState, observation, at, prevLockedSince);

    const transitions = prev?.transitions ?? [];
    const nextTransitions = appendTransition(transitions, result.transition);

    const lockedSince = result.lockState.lockedSince;
    this.records.set(key, {
      state: result.next,
      lockedSince,
      transitions: nextTransitions,
    });

    return {
      selected: result.selected,
      nextState: result.next,
      transition: result.transition,
      lockState: result.lockState,
    };
  }
}

function appendTransition(
  existing: ReadonlyArray<ChatSourceTransition>,
  next: ChatSourceTransition,
): ChatSourceTransition[] {
  // Collapse consecutive no-op transitions of the same shape so the buffer
  // does not fill with `NativeLocked -> NativeLocked / NoOp` every readChat.
  const last = existing[existing.length - 1];
  if (
    last
    && last.fromState === next.fromState
    && last.toState === next.toState
    && last.event === next.event
    && last.cause === next.cause
  ) {
    return existing as ChatSourceTransition[];
  }
  const trimmed = existing.length >= TRANSITION_HISTORY_LIMIT
    ? existing.slice(existing.length - TRANSITION_HISTORY_LIMIT + 1)
    : [...existing];
  trimmed.push(next);
  return trimmed;
}

// ─── Observation builders ───────────────────────────────────────────────

/**
 * Build a native_present observation from a v2 read_chat payload's message
 * array. v2 producers already provide stable identity (transcript-v2.ts
 * invariants), so this is a thin projection.
 */
export function buildV2NativePresentObservation(args: {
  messages: ReadonlyArray<{ providerUnitKey: string; sequence: number }>;
  coverage: 'full' | 'tail' | 'current-turn' | 'partial';
  safeMapping: boolean;
}): ChatSourceObservation {
  return {
    kind: 'native_present',
    contractVersion: CHAT_CONTRACT_VERSION_V2,
    messages: args.messages.map(toIdentity),
    coverage: args.coverage,
    safeMapping: args.safeMapping,
  };
}

/**
 * Build a native_present observation from v1 producer output. v1 messages
 * may have no providerUnitKey or a producer-derived one that shifts across
 * reads (index/content-hash based). We synthesise a deterministic identity
 * from the most stable fields available so the machine has *something* to
 * compare across reads. This is intentionally lossy — A2.3 retires v1
 * producers and this branch goes away.
 *
 * The synthesised key is NOT the same as the v2 contract key; never compare
 * v1-synthesised keys against v2 keys for the same logical message. The
 * registry naturally separates them by ChatSourceSessionKey so cross-version
 * leakage cannot happen in practice.
 */
export function buildV1NativePresentObservation(args: {
  providerType: string;
  sessionId: string;
  messages: ReadonlyArray<{
    providerUnitKey?: unknown;
    bubbleId?: unknown;
    id?: unknown;
    index?: unknown;
    role?: unknown;
    receivedAt?: unknown;
    timestamp?: unknown;
    content?: unknown;
  }>;
  coverage: 'full' | 'tail' | 'current-turn' | 'partial';
  safeMapping: boolean;
}): ChatSourceObservation {
  const identities: ResolverNativeMessageIdentity[] = [];
  let synthesisedSequence = 0;
  for (const message of args.messages) {
    const providerUnitKey = pickFirstString(
      message.providerUnitKey,
      message.bubbleId,
      message.id,
    ) ?? synthesiseV1UnitKey(args.providerType, args.sessionId, synthesisedSequence, message);
    const sequence = pickFiniteNumber(message.receivedAt, message.timestamp, message.index)
      ?? synthesisedSequence;
    identities.push({ providerUnitKey, sequence });
    synthesisedSequence += 1;
  }
  return {
    kind: 'native_present',
    contractVersion: CHAT_CONTRACT_VERSION_V1,
    messages: identities,
    coverage: args.coverage,
    safeMapping: args.safeMapping,
  };
}

function toIdentity(message: { providerUnitKey: string; sequence: number }): ResolverNativeMessageIdentity {
  return { providerUnitKey: message.providerUnitKey, sequence: message.sequence };
}

function pickFirstString(...candidates: unknown[]): string | undefined {
  for (const c of candidates) {
    if (typeof c === 'string' && c.length > 0) return c;
  }
  return undefined;
}

function pickFiniteNumber(...candidates: unknown[]): number | undefined {
  for (const c of candidates) {
    if (typeof c === 'number' && Number.isFinite(c)) return c;
  }
  return undefined;
}

/**
 * Deterministic v1 fallback unit key. Stable across reads of the same
 * (providerType, sessionId) for the same logical message *as long as the
 * v1 producer keeps emitting it at the same position in the array*. v1
 * producers do not all do that (the v1 audit found three different index-
 * sensitive ID schemes), but a stable-when-stable key is still better than
 * a fresh hash every read.
 *
 * If the message carries any of bubbleId/id/providerUnitKey, those win over
 * this fallback — the caller of buildV1NativePresentObservation has already
 * applied that precedence.
 */
function synthesiseV1UnitKey(
  providerType: string,
  sessionId: string,
  positionalSeq: number,
  message: {
    role?: unknown;
    receivedAt?: unknown;
    timestamp?: unknown;
    content?: unknown;
  },
): string {
  const role = typeof message.role === 'string' ? message.role : '';
  const ts = pickFiniteNumber(message.receivedAt, message.timestamp);
  // Position is included so two structurally identical messages in the same
  // session do not collide; ts when available pins the key to the original
  // ingest moment rather than the current read's array layout.
  return `v1:${providerType}:${sessionId}:${ts ?? ''}:${positionalSeq}:${role}`;
}

// ─── Module-level registry (production singleton) ───────────────────────

export const CHAT_SOURCE_REGISTRY = new ChatSourceRegistry();

// ─── Re-exports for one-stop import ─────────────────────────────────────

export type {
  ChatSourceDecision,
  ChatSourceLockState,
  ChatSourceObservation,
  ChatSourceState,
  ChatSourceTransition,
  ChatSourceTransitionCause,
  ResolverNativeMessageIdentity,
} from './source-machine.js';
export { transitionChatSourceState, INITIAL_CHAT_SOURCE_STATE };
export type { ChatContractVersion };
